import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { FiCreditCard, FiAlertCircle } from 'react-icons/fi';
import '@ranty/ranty-sdk';
import { getStatusColor } from '../utils/theme-utils';
import config from '../config/config';

// Nave SDK Event Types based on @ranty/ranty-sdk documentation
export enum NaveEventType {
  AUTH_ERROR = 'AUTH_ERROR',
  PAYMENT_REQUEST_ERROR = 'PAYMENT_REQUEST_ERROR',
  SUCCESS_PROCESSED = 'SUCCESS_PROCESSED',
  FAILURE_PROCESSED = 'FAILURE_PROCESSED',
  PENDING_PROCESSED = 'PENDING_PROCESSED',
  CANCELLED = 'CANCELLED',
  // Additional events for loading/processing states
  LOADING = 'LOADING',
  PROCESSING = 'PROCESSING',
  READY = 'READY',
}

export interface NaveEventData {
  type: NaveEventType;
  message?: string;
  data?: {
    payment_id?: string;
    status?: string;
    [key: string]: unknown;
  };
}

export interface NaveCheckoutResult {
  success: boolean;
  paymentId?: string;
  status: 'approved' | 'rejected' | 'pending' | 'cancelled' | 'error';
  message?: string;
}

interface NaveCheckoutProps {
  paymentRequestId: string; // UUID v4 identifier for the payment request (from backend)
  publicKey?: string; // Optional override for the public key
  onPaymentComplete: (result: NaveCheckoutResult) => void;
  onError: (error: string) => void;
  onProcessing?: (isProcessing: boolean) => void;
  disabled?: boolean;
  showTitle?: boolean;
  showSubtitle?: boolean;
  showOrderDetail?: boolean;
  enableAutoRedirect?: boolean;
  staging?: boolean; // Force staging environment
}

/**
 * NaveCheckout - Nave (Naranja X) payment integration component
 *
 * Theme Support: ✅ Full light/dark mode support
 * Colors Used: bg-surface-base, bg-surface-secondary, text-text-primary, text-text-secondary, border-border-primary
 * Status Colors: Uses getStatusColor() for dynamic states
 *
 * This component integrates with the @ranty/ranty-sdk to provide a payment checkout
 * experience powered by Naranja X / Nave.
 */
const NaveCheckout: React.FC<NaveCheckoutProps> = ({
  paymentRequestId,
  publicKey,
  onPaymentComplete,
  onError,
  onProcessing,
  disabled = false,
  showTitle = true,
  showSubtitle = true,
  showOrderDetail = true,
  enableAutoRedirect = false,
  staging,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sdkElementRef = useRef<HTMLElement | null>(null);

  // Determine environment - use staging in non-production or if explicitly set
  const isStaging = staging ?? !config.production;
  const navePublicKey = publicKey || config.navePublicKey;

  // SDK settings configuration
  const sdkSettings = JSON.stringify({
    show_title: showTitle,
    show_subtitle: showSubtitle,
    show_order_detail: showOrderDetail,
    enable_auto_redirect: enableAutoRedirect,
  });

  // Handle SDK events
  const handleSDKEvent = useCallback(
    (event: MessageEvent<NaveEventData>) => {
      // Validate event origin and data
      if (!event.data || typeof event.data.type !== 'string') {
        return;
      }

      console.log('[Nave] Received event from SDK:', event.data);

      const { type, message, data } = event.data;

      switch (type) {
        case NaveEventType.SUCCESS_PROCESSED:
          setIsProcessing(false);
          onProcessing?.(false);
          onPaymentComplete({
            success: true,
            paymentId: data?.payment_id,
            status: 'approved',
            message: message || 'Pago aprobado exitosamente',
          });
          break;

        case NaveEventType.FAILURE_PROCESSED:
          setIsProcessing(false);
          onProcessing?.(false);
          onPaymentComplete({
            success: false,
            paymentId: data?.payment_id,
            status: 'rejected',
            message: message || 'El pago fue rechazado',
          });
          break;

        case NaveEventType.PENDING_PROCESSED:
          setIsProcessing(false);
          onProcessing?.(false);
          onPaymentComplete({
            success: false,
            paymentId: data?.payment_id,
            status: 'pending',
            message: message || 'El pago está pendiente de confirmación',
          });
          break;

        case NaveEventType.CANCELLED:
          setIsProcessing(false);
          onProcessing?.(false);
          onPaymentComplete({
            success: false,
            status: 'cancelled',
            message: message || 'El pago fue cancelado',
          });
          break;

        case NaveEventType.AUTH_ERROR:
          setIsProcessing(false);
          onProcessing?.(false);
          onError(message || 'Error de autenticación con el sistema de pagos');
          break;

        case NaveEventType.PAYMENT_REQUEST_ERROR:
          setIsProcessing(false);
          onProcessing?.(false);
          onError(message || 'Error al procesar la solicitud de pago');
          break;

        case NaveEventType.PROCESSING:
        case NaveEventType.LOADING:
          // User has submitted the payment form, now processing
          setIsProcessing(true);
          onProcessing?.(true);
          break;

        case NaveEventType.READY:
          // SDK is ready, form is loaded (no longer processing)
          setIsProcessing(false);
          onProcessing?.(false);
          break;

        default:
          console.log('[Nave] Unhandled event type:', type);
      }
    },
    [onPaymentComplete, onError, onProcessing],
  );

  // Create and render the SDK element
  useEffect(() => {
    if (!containerRef.current || !paymentRequestId || !navePublicKey) {
      return undefined;
    }

    console.log('[Nave] Environment:', isStaging ? 'staging' : 'production');
    console.log('[Nave] Public Key:', navePublicKey ? `${navePublicKey.slice(0, 10)}...` : 'NOT SET');

    // Remove existing element if any
    if (sdkElementRef.current) {
      sdkElementRef.current.remove();
      sdkElementRef.current = null;
    }

    // Create the payfac-sdk custom element
    const sdkElement = document.createElement('payfac-sdk');
    sdkElement.setAttribute('paymentRequestId', paymentRequestId);
    sdkElement.setAttribute('publicKey', navePublicKey);
    sdkElement.setAttribute('settings', sdkSettings);

    // Set environment to staging if not in production
    if (isStaging) {
      sdkElement.setAttribute('env', 'staging');
    }

    // Apply some styling to the container
    sdkElement.style.width = '100%';
    sdkElement.style.minHeight = '400px';
    sdkElement.style.display = 'block';

    containerRef.current.appendChild(sdkElement);
    sdkElementRef.current = sdkElement;

    console.log('[Nave] SDK element rendered with paymentRequestId:', paymentRequestId);

    // Note: isProcessing should only be true when actively processing a payment,
    // not when the SDK form is simply loaded. The SDK will send events when
    // a payment is actually being processed.

    // Cleanup function
    return () => {
      if (sdkElementRef.current) {
        sdkElementRef.current.remove();
        sdkElementRef.current = null;
      }
    };
  }, [paymentRequestId, navePublicKey, sdkSettings, isStaging, onProcessing]);

  // Listen for SDK events via postMessage
  useEffect(() => {
    window.addEventListener('message', handleSDKEvent);

    return () => {
      window.removeEventListener('message', handleSDKEvent);
    };
  }, [handleSDKEvent]);

  const errorStyles = getStatusColor('error');

  // Error state - Missing configuration
  if (!navePublicKey) {
    return (
      <div className={`glass-surface rounded-3xl p-6 flex flex-col items-center justify-center min-h-[200px] ${errorStyles.bg}`}>
        <FiAlertCircle className={`text-4xl ${errorStyles.icon} mb-4`} />
        <p className={errorStyles.text}>Configuración de Nave incompleta</p>
        <p className="text-text-tertiary text-sm mt-2">Falta la clave pública de Nave</p>
      </div>
    );
  }

  // Error state - Missing payment request ID
  if (!paymentRequestId) {
    return (
      <div className={`glass-surface rounded-3xl p-6 flex flex-col items-center justify-center min-h-[200px] ${errorStyles.bg}`}>
        <FiAlertCircle className={`text-4xl ${errorStyles.icon} mb-4`} />
        <p className={errorStyles.text}>Error de configuración</p>
        <p className="text-text-tertiary text-sm mt-2">No se recibió el ID de solicitud de pago</p>
      </div>
    );
  }

  return (
    <div className="glass-surface w-full lg:p-3">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <FiCreditCard className="text-2xl text-primary-500" />
        <h2 className="text-lg font-semibold text-text-primary">Pagar con Nave</h2>
        {isStaging && (
          <span className="ml-auto text-xs bg-warning-100 text-warning-600 px-2 py-1 rounded-full">
            Modo prueba
          </span>
        )}
      </div>

      {/* SDK Container */}
      <div
        ref={containerRef}
        className={`
          rounded-xl overflow-hidden bg-surface-secondary
          ${disabled ? 'opacity-50 pointer-events-none' : ''}
        `}
        style={{ minHeight: '400px' }}
      />

      {/* Processing indicator */}
      {isProcessing && (
        <div className="flex items-center justify-center gap-2 mt-4 text-text-secondary text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
          <span>Procesando pago...</span>
        </div>
      )}
    </div>
  );
};

export default NaveCheckout;
